import type { ParentData } from "features/cases/blocks/CommonCard/CommonCard.types";
import { useIsMobile } from "hooks/useIsMobile";
import { useTexts } from "hooks/useTexts";
import React, { ReactNode } from "react";
import commentIcon from "static/icons/comment.svg";
import editIcon from "static/icons/edit.svg";
import BubbleButton from "ui/BubbleButton";
import Spacer from "ui/Spacer";

type CardFooterProps = {
  isNewCard: boolean | undefined;
  parentData: ParentData;
  hasButtons: boolean;
  variantString: string;
  showEdit: boolean;
  editUrl?: string;
  url?: string;
  linkTarget: string;
  onLinkClick?: () => void;
  linkButtonText?: ReactNode;
  embedded?: boolean;
  canEdit: boolean;
  addUrl?: string;
  desktopOrNewCard: boolean;
  isEditing: boolean;
  editingButtons: ReactNode;
  editButtonClick: () => void;
  showComments: boolean;
  showBottomToggle: boolean;
  toggleClick: () => void;
  toggleText: ReactNode;
};

const CardFooter: React.FC<CardFooterProps> = ({
  isNewCard,
  parentData,
  hasButtons,
  variantString,
  showEdit,
  editUrl,
  url,
  linkTarget,
  onLinkClick,
  linkButtonText,
  embedded,
  canEdit,
  addUrl,
  desktopOrNewCard,
  isEditing,
  editingButtons,
  editButtonClick,
  showComments,
  showBottomToggle,
  toggleClick,
  toggleText,
}) => {
  const { txt } = useTexts("case");
  const { isMobile } = useIsMobile();

  if (isNewCard) {
    const newElementName = () => {
      if (parentData.isProblem)
        return txt("add_new_problem", "Добавить новую проблему");
      if (parentData.isSolution)
        return txt("add_new_solution", "Добавить новое решение");
      if (parentData.isAttribute)
        return txt("add_new_attribute", "Добавить новый атрибут");

      return txt("add_new", "Добавить");
    };

    return (
      <Spacer
        rightmost={false}
        withSeparator={false}
      >
        <div className="card-new-element-text">{newElementName()}</div>
      </Spacer>
    );
  }

  if (!hasButtons && !variantString) return null;

  return (
    <Spacer
      rightmost={hasButtons || !!variantString}
      withSeparator={true}
    >
      {showEdit && (
        <div className="editor">
          {editUrl && (
            <p>
              <a
                href={editUrl}
                target="_blank"
                rel="noreferrer"
              >
                [E]
              </a>
            </p>
          )}
        </div>
      )}

      {!embedded && !!url && (
        <a
          className="buttonlike-link"
          href={url}
          target={linkTarget}
          rel="noreferrer"
          onClick={onLinkClick}
        >
          {linkButtonText}
        </a>
      )}
      {!!variantString && <em>{variantString}</em>}

      <span style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
        {canEdit &&
          (editUrl || addUrl) &&
          (embedded ? (
            desktopOrNewCard || isEditing ? (
              <>{editingButtons}</>
            ) : null
          ) : (
            <BubbleButton
              className="right"
              size="m"
              onClick={editButtonClick}
              icon={editIcon}
              autosize={false}
              square={false}
            />
          ))}

        {showComments && !embedded && !isMobile && !isEditing && (
          <BubbleButton
            className="right"
            size="m"
            onClick={editButtonClick}
            icon={commentIcon}
            autosize={false}
            square={false}
          />
        )}

        {showBottomToggle && (
          <BubbleButton
            className="right"
            size="m"
            onClick={toggleClick}
            text={toggleText}
            autosize={false}
            square={true}
          />
        )}
      </span>
    </Spacer>
  );
};

export default CardFooter;
